import React, {useCallback, useState} from 'react';
import {Input, Tag, Typography} from 'antd';
import {useIntl} from 'umi';
import {addZipCodes, ZIP_CODE_MAX_LENGTH} from '@/utils/location';
import styles from './index.less';

interface ZipCodesInputProps {
  value?: string[];
  onChange?: (zipCodes: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

const EMPTY: string[] = [];
const SEPARATOR_KEYS = ['Enter', ',', ';'];

const ZipCodesInput: React.FC<ZipCodesInputProps> = ({value = EMPTY, onChange, disabled, placeholder}) => {
  const intl = useIntl();
  const [draft, setDraft] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const commit = useCallback((input: string) => {
    const {zipCodes, duplicates, tooLong} = addZipCodes(value, input);
    const nextErrors: string[] = [];
    if (duplicates.length) {
      nextErrors.push(intl.formatMessage(
        {id: 'pages.locations.zipCodes.duplicate', defaultMessage: 'Already added: {zipCodes}'},
        {zipCodes: duplicates.join(', ')},
      ));
    }
    if (tooLong.length) {
      nextErrors.push(intl.formatMessage(
        {id: 'pages.locations.zipCodes.tooLong', defaultMessage: 'Longer than {max} characters: {zipCodes}'},
        {max: ZIP_CODE_MAX_LENGTH, zipCodes: tooLong.join(', ')},
      ));
    }
    setErrors(nextErrors);
    // Rejected long codes stay in the field so they can be shortened instead of retyped.
    setDraft(tooLong.join(', '));
    if (zipCodes.length !== value.length) onChange?.(zipCodes);
  }, [value, onChange, intl]);

  const remove = useCallback((zipCode: string) => {
    setErrors([]);
    onChange?.(value.filter((z) => z !== zipCode));
  }, [value, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!SEPARATOR_KEYS.includes(e.key)) return;
    e.preventDefault();
    if (draft.trim()) commit(draft);
  }, [draft, commit]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    commit(`${draft},${e.clipboardData.getData('text')}`);
  }, [draft, commit]);

  const handleBlur = useCallback(() => {
    if (draft.trim()) commit(draft);
  }, [draft, commit]);

  return (
    <div data-submit-on-enter="false">
      {value.length > 0 && (
        <div className={styles.zipCodeTags}>
          {value.map((zipCode) => (
            <Tag
              key={zipCode}
              closable={!disabled}
              onClose={(e) => {
                // The list is controlled by `value`; don't let Tag hide itself before the save lands.
                e.preventDefault();
                remove(zipCode);
              }}
            >
              {zipCode}
            </Tag>
          ))}
        </div>
      )}
      <Input
        value={draft}
        disabled={disabled}
        status={errors.length ? 'error' : undefined}
        placeholder={placeholder}
        onChange={(e) => {
          setDraft(e.target.value);
          if (errors.length) setErrors([]);
        }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
      />
      {errors.map((error) => (
        <Typography.Text key={error} type="danger" className={styles.zipCodeError}>
          {error}
        </Typography.Text>
      ))}
    </div>
  );
};

export default React.memo(ZipCodesInput);
